'use client';
import { useState, ComponentProps } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { DragAndDropBox } from './drag-and-drop-box';
import FileUploadSampleFiles from 'components/file-upload-sample-files';
import {
  CatFileUpload,
  BlocksFileAddEvent
} from 'components/blocks-components';
import { Conditional, useBreakpoint } from 'components/pcc-components';
import { validateFile } from './utils';
import { FILE_FORMATS, PAGE_BREAKPOINTS } from 'constants/common';
import { CREATE_LIST_MODAL_FIELDS, FILE_SIZE_LIMIT } from '../constants/common';
import { FilesStateConfig } from 'blocks-nextjs/blocks-web-components/components/file-upload/file-upload.model';
import { ErrorFieldNote } from './error-field-note';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';

type ExtendedFileUploadProps = ComponentProps<typeof CatFileUpload> & {
  filesState: FilesStateConfig[] | [];
};
type EquipmentSavedListProps = { isAssociateEquipmentToSavedList?: boolean };

export const CreateListFileUpload = ({
  isAssociateEquipmentToSavedList
}: EquipmentSavedListProps) => {
  const t = useTranslations();
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const { FILE_NAME } = CREATE_LIST_MODAL_FIELDS;
  const {
    register,
    watch,
    setValue,
    setError,
    resetField,
    formState: { errors }
  } = useFormContext();
  const [isDisabled, setIsDisabled] = useState(false);
  const errorText = errors?.[FILE_NAME]?.message || '';
  const fileValue = watch(FILE_NAME);
  const fileValueWithRemoveBtn = fileValue?.map((items) => ({
    ...items,
    action: 'cancel',
    actionText: t('REMOVE')
  }));
  const statusText = t('FILE_ADDED');

  const handleFileUpload = ({ detail }: BlocksFileAddEvent): void => {
    setIsDisabled(true);
    const { data, error } = validateFile(detail, statusText);
    setError(FILE_NAME, { message: t(error) });
    setValue(FILE_NAME, data);
    setIsDisabled(false);
  };

  const handleFileRemove = () => {
    resetField(FILE_NAME);
  };

  const fileUploadProps = {
    ...register(FILE_NAME),
    uploadFiles: fileValueWithRemoveBtn,
    fileSizeLimit: FILE_SIZE_LIMIT,
    accept: `.${FILE_FORMATS.csv},.${FILE_FORMATS.xlsx}`,
    multiple: false,
    'data-testId': 'list-file-upload',
    onBlFileAdd: handleFileUpload,
    onBlFileAction: handleFileRemove,
    disabled: isDisabled,
    isSmallTable: true,
    isReplace: true,
    variant: isDesktop ? null : 'button',
    filesState: fileValueWithRemoveBtn
  };

  return (
    <div className="cat-u-padding-bottom-xl d-block pt-3">
      <label className="cat-u-theme-typography-label">
        <Conditional
          test={isAssociateEquipmentToSavedList}
          fallback={t('ADD_PARTS_TO_YOUR_LIST')}
        >
          {t('ADD_PARTS_FROM_FILE')}{' '}
          <span className="cat-u-theme-typography-footnote">
            {t('OPTIONAL_LABEL_LOW_CASE')}
          </span>
        </Conditional>
      </label>
      {/* TODO - Input Validation - Display allowed files only in native window
        TODO - Input Validation - Add type attribute and allow only csv and xlxs file extensions only */}
      <CatFileUpload required {...(fileUploadProps as ExtendedFileUploadProps)}>
        <FileUploadSampleFiles />
        <DragAndDropBox
          isDisabled={isDisabled}
          isAssociateEquipmentToSavedList={isAssociateEquipmentToSavedList}
        />
      </CatFileUpload>
      <Conditional test={!isEmpty(errorText)}>
        <ErrorFieldNote error={errorText as string} />
      </Conditional>
    </div>
  );
};
